import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, resolveTenantSlug } from "../lib/api";
import { getPreset, getFontPair, hexToHslTriplet, DEFAULT_PRESET } from "../theme/presets";

/**
 * BrandingContext — exposes admin-controlled app branding (name, logo,
 * colours, theme preset) to every component in the tree. Loaded once on
 * mount, refetched via `refresh()` after admin edits. Also injects a
 * dynamic <style> tag so the active theme preset's CSS variables override
 * the defaults globally.
 */

const DEFAULTS = {
  brand_name: "KalkiBazaar",
  short_name: "Kalki",
  tagline: "Couture, connected.",
  logo_square_url: null,
  logo_horizontal_url: null,
  // Colour overrides — null means "use the active preset's value".
  primary_color: null,
  accent_color: null,
  background_color: null,
  surface_color: null,
  theme_color: null,         // PWA chrome (Android address bar); falls back to primary_color
  theme_preset: DEFAULT_PRESET,
};

const BrandingContext = createContext({
  branding: DEFAULTS,
  loading: true,
  refresh: async () => {},
});

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/app");
      setBranding({ ...DEFAULTS, ...data });
    } catch {
      setBranding(DEFAULTS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Apply dynamic document.title base, favicon, and PWA manifest
  useEffect(() => {
    if (loading) return;

    // Update <link rel="manifest">
    let link = document.querySelector('link[rel="manifest"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "manifest";
      document.head.appendChild(link);
    }
    // Point the <link> to the dynamic backend manifest. Include the
    // resolved tenant slug as a query param so server-side PWA crawlers
    // (PWA Builder, Lighthouse, Play Store install flow, etc.) reach the
    // correct tenant's manifest — they can't replay the X-Tenant-Slug
    // request header from our axios interceptor.
    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    const slug = resolveTenantSlug();
    link.href = `${apiBase}/api/settings/manifest.webmanifest${slug ? `?tenant=${encodeURIComponent(slug)}` : ""}`;

    // Favicon — reuse square logo when set
    if (branding.logo_square_url) {
      let fav = document.querySelector('link[rel="icon"]');
      if (!fav) {
        fav = document.createElement("link");
        fav.rel = "icon";
        document.head.appendChild(fav);
      }
      fav.href = branding.logo_square_url;
    }

    // theme-color meta
    let themeMeta = document.querySelector('meta[name="theme-color"]');
    if (!themeMeta) {
      themeMeta = document.createElement("meta");
      themeMeta.name = "theme-color";
      document.head.appendChild(themeMeta);
    }
    themeMeta.content = branding.theme_color || branding.primary_color || "#7c3a4b";

    // apple-mobile-web-app-title (for iOS pinned)
    let appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!appleTitle) {
      appleTitle = document.createElement("meta");
      appleTitle.name = "apple-mobile-web-app-title";
      document.head.appendChild(appleTitle);
    }
    appleTitle.content = branding.short_name || branding.brand_name;

    // Base document.title — always sync to the current brand name so a
    // brand rename takes effect immediately on every tab. Pages with their
    // own useEffect setting document.title will still override on render.
    if (branding?.brand_name) {
      document.title = branding.brand_name;
    }
  }, [branding, loading]);

  // Apply the storefront theme preset by injecting a <style> tag that
  // overrides the CSS variables. Also load the preset's Google Fonts.
  useEffect(() => {
    if (loading) return;
    const preset = getPreset(branding?.theme_preset);
    // Use the tenant's chosen font pair (or the preset's recommended default).
    const fontPair = getFontPair(branding?.theme_preset, branding?.font_pair) || {
      display: preset.fonts?.display, body: preset.fonts?.body,
      googleFamilies: preset.fonts?.googleFamilies || [],
    };

    // 1. Inject CSS-variable overrides — preset baseline, then tenant
    //    colour overrides on top so a Primary/Accent/Background/Surface
    //    customisation wins over the preset's defaults.
    let style = document.getElementById("kb-theme-vars");
    if (!style) {
      style = document.createElement("style");
      style.id = "kb-theme-vars";
      document.head.appendChild(style);
    }
    const presetVars = { ...preset.vars };
    const OVERRIDE_MAP = {
      primary_color: "--kb-primary",
      accent_color: "--kb-accent",
      background_color: "--kb-bg",
      surface_color: "--kb-surface",
    };
    for (const [field, cssVar] of Object.entries(OVERRIDE_MAP)) {
      const hex = branding?.[field];
      const triplet = hexToHslTriplet(hex);
      if (triplet) presetVars[cssVar] = triplet;
    }
    // Auto-derive primary-hover from primary by darkening L by 10pp when
    // an override is present — keeps hover states visually consistent.
    const primaryHex = branding?.primary_color;
    if (primaryHex) {
      const primaryTriplet = hexToHslTriplet(primaryHex);
      if (primaryTriplet) {
        const m = primaryTriplet.match(/^(\S+)\s+(\S+)\s+(\d+)%$/);
        if (m) {
          const darker = Math.max(0, parseInt(m[3], 10) - 10);
          presetVars["--kb-primary-hover"] = `${m[1]} ${m[2]} ${darker}%`;
        }
      }
    }
    const varBlock = Object.entries(presetVars)
      .map(([k, v]) => `  ${k}: ${v};`)
      .join("\n");
    style.textContent = `:root {\n${varBlock}\n  --radius: ${preset.radius};\n}\n` +
      `body { font-family: ${fontPair.body}; }\n` +
      `.font-display { font-family: ${fontPair.display}; }\n`;

    // 2. Load Google Fonts for this preset+pair (idempotent, dedup by id)
    const fontsId = "kb-theme-fonts";
    let fontsLink = document.getElementById(fontsId);
    const families = (fontPair.googleFamilies || [])
      .map((f) => `family=${f}`)
      .join("&");
    const href = families
      ? `https://fonts.googleapis.com/css2?${families}&display=swap`
      : "";
    if (href) {
      if (!fontsLink) {
        fontsLink = document.createElement("link");
        fontsLink.id = fontsId;
        fontsLink.rel = "stylesheet";
        document.head.appendChild(fontsLink);
      }
      if (fontsLink.href !== href) fontsLink.href = href;
    }

    // 3. Expose the active preset on <html data-theme=...> for any
    //    preset-specific CSS overrides we may want later.
    document.documentElement.setAttribute("data-theme", branding?.theme_preset || "kalki");
  }, [branding, loading]);

  return (
    <BrandingContext.Provider value={{ branding, loading, refresh }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
